// ── Announcements that reach the contestant's screen (`EX-ANN01`) ──────────
// An announcement used to be read only on the contest page's Announcements
// tab, and a contestant spends the round in the problem workspace, so "the
// contest is extended by 15 minutes" reached nobody who was coding. Every
// contest page now polls the announcements while the round is live and shows
// the newest one this browser has not acknowledged.
//
// **What "seen" means.** One instant per contest per browser: the newest
// `editedAt ?? postedAt` the viewer acknowledged. Anything stamped later is
// new, so an announcement the organizer corrects comes back. It lives in
// `localStorage` and every access is wrapped: a blocked or private store only
// means the alert shows again, never that the page breaks (`contest-guide.ts`).
//
// No email and no push (`docs/29` §12): a contestant is on these pages for the
// whole window, and a fan-out per announcement is a job of its own.
//
// `EX-ANN02`: an answered clarification reaches the page the same way, with
// its own marker (the answer's `answeredAt`); see the end of this file.

import type { ContestAnnouncement, ContestClarification, ContestPhase } from "../types";

const SEEN_KEY_PREFIX = "progsity:contest:announcements-seen:";

export function announcementsSeenKey(slug: string): string {
  return `${SEEN_KEY_PREFIX}${slug}`;
}

/** When an announcement last changed: an edit makes it new again. */
export function announcementStamp(item: Pick<ContestAnnouncement, "postedAt" | "editedAt">): number {
  return Date.parse(item.editedAt ?? item.postedAt);
}

/** Newest first; only what changed after the instant this browser acknowledged. */
export function unseenAnnouncements(items: readonly ContestAnnouncement[], seenAt: number | null): ContestAnnouncement[] {
  return items
    .filter((item) => seenAt === null || announcementStamp(item) > seenAt)
    .sort((a, b) => announcementStamp(b) - announcementStamp(a));
}

export function newestAnnouncementStamp(items: readonly ContestAnnouncement[]): number | null {
  const stamps = items.map(announcementStamp).filter(Number.isFinite);
  return stamps.length ? Math.max(...stamps) : null;
}

/** The acknowledged instant stored under `key`, or null when nothing was acknowledged. */
export function readSeenMarker(key: string): number | null {
  if (typeof window === "undefined") return null;
  try {
    const value = Number(window.localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

export function rememberSeenMarker(key: string, at: number): void {
  try {
    window.localStorage.setItem(key, String(at));
  } catch {
    // A blocked store: the alert comes back on the next read, which costs one click.
  }
}

export function readAnnouncementsSeen(slug: string): number | null {
  return readSeenMarker(announcementsSeenKey(slug));
}

export function rememberAnnouncementsSeen(slug: string, at: number): void {
  rememberSeenMarker(announcementsSeenKey(slug), at);
}

export type AnnouncementWindow = { phase?: ContestPhase | string; isOpen?: boolean; endAt?: string | null };

/** The round is on, or ended under an hour ago (the extension's grace): poll fast. */
export function announcementsLive(contest: AnnouncementWindow | undefined, now = Date.now()): boolean {
  if (!contest) return false;
  if (contest.isOpen || contest.phase === "running" || contest.phase === "frozen") return true;
  if (contest.phase !== "ended" || !contest.endAt) return false;
  return now - Date.parse(contest.endAt) <= 60 * 60_000;
}

/** Where the alert shows: a live round, and before the start ("we start 20 minutes late"). */
export function announcementsAlerting(contest: AnnouncementWindow | undefined, now = Date.now()): boolean {
  return contest?.phase === "scheduled" || announcementsLive(contest, now);
}

// ── Answered clarifications (`EX-ANN02`) ─────────────────────────────────────
// A contestant who asked waits in the workspace, and a broadcast answer
// ("in problem B, n can be 0") matters to everybody coding. Only an answered
// clarification is news; the stamp is the answer's own instant.

const CLARIFICATIONS_SEEN_KEY_PREFIX = "progsity:contest:clarifications-seen:";

export function clarificationsSeenKey(slug: string): string {
  return `${CLARIFICATIONS_SEEN_KEY_PREFIX}${slug}`;
}

function answerStamp(item: Pick<ContestClarification, "answer" | "answeredAt">): number {
  return item.answer && item.answeredAt ? Date.parse(item.answeredAt) : Number.NaN;
}

/** Answered after the acknowledged instant, newest answer first. */
export function unseenClarificationAnswers(items: readonly ContestClarification[], seenAt: number | null): ContestClarification[] {
  return items
    .filter((item) => Number.isFinite(answerStamp(item)) && (seenAt === null || answerStamp(item) > seenAt))
    .sort((a, b) => answerStamp(b) - answerStamp(a));
}

export function newestClarificationAnswerStamp(items: readonly ContestClarification[]): number | null {
  const stamps = items.map(answerStamp).filter(Number.isFinite);
  return stamps.length ? Math.max(...stamps) : null;
}
